import { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";

export interface SelectOption {
  value: string;
  label: string;
}

interface CustomSelectProps {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  emptyLabel?: string;
  allowEmpty?: boolean;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
}

export function CustomSelect({
  value,
  options,
  onChange,
  emptyLabel = "—",
  allowEmpty = true,
  disabled = false,
  className = "",
  placeholder,
  searchable = false,
  searchPlaceholder = "Buscar…",
}: CustomSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const selected = options.find(o => o.value === value) ?? null;

  const filteredOptions = useMemo(() => {
    if (!searchable || !search.trim()) return options;
    const term = search.trim().toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(term));
  }, [options, search, searchable]);

  function openDropdown() {
    if (disabled) return;
    if (triggerRef.current) setRect(triggerRef.current.getBoundingClientRect());
    setOpen(v => !v);
    setSearch("");
  }

  useEffect(() => {
    if (open && searchable) {
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [open, searchable]);

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (
        triggerRef.current?.contains(e.target as Node) ||
        dropdownRef.current?.contains(e.target as Node)
      ) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  const displayLabel = selected?.label ?? placeholder ?? emptyLabel;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`custom-select-trigger${open ? " is-open" : ""}${disabled ? " is-disabled" : ""} ${className}`.trim()}
        onClick={openDropdown}
        disabled={disabled}
      >
        <span className="custom-select-value">{displayLabel}</span>
        <svg
          className="custom-select-chevron"
          xmlns="http://www.w3.org/2000/svg"
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && rect && createPortal(
        <div
          ref={dropdownRef}
          className="custom-select-dropdown"
          style={{ top: rect.bottom + 4, left: rect.left, minWidth: Math.max(rect.width, 140) }}
        >
          {searchable && (
            <div className="custom-select-search">
              <input
                ref={searchRef}
                type="text"
                className="custom-select-search-input"
                placeholder={searchPlaceholder}
                value={search}
                onChange={e => setSearch(e.target.value)}
                onClick={e => e.stopPropagation()}
              />
            </div>
          )}
          {allowEmpty && !search && (
            <button
              type="button"
              className={`custom-select-option${!value ? " is-selected" : ""}`}
              onClick={() => { onChange(""); setOpen(false); }}
            >
              {emptyLabel}
            </button>
          )}
          {filteredOptions.length === 0 && (
            <div className="custom-select-no-results">Sin resultados</div>
          )}
          {filteredOptions.map(o => (
            <button
              key={o.value}
              type="button"
              className={`custom-select-option${o.value === value ? " is-selected" : ""}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {o.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}
